import React, { useEffect, useMemo, useRef, useState } from "react";
import { onToast } from "../api";

function clamp(n, a, b) {
  return Math.max(a, Math.min(b, n));
}

function typeMeta(type) {
  const t = (type || "info").toLowerCase();
  if (t === "success") return { label: "تم", icon: "✓" };
  if (t === "error") return { label: "خطأ", icon: "⛔" };
  if (t === "warning") return { label: "تنبيه", icon: "⚠️" };
  return { label: "معلومة", icon: "ℹ️" };
}

export default function ToastHost() {
  const [items, setItems] = useState([]);
  const timersRef = useRef(new Map());

  const styles = useMemo(() => {
    return {
      wrap: {
        position: "fixed",
        top: 16,
        right: 16,
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        maxWidth: 420,
        direction: "rtl",
        alignItems: "flex-end",
        pointerEvents: "none",
      },
      toast: {
        pointerEvents: "auto",
        width: "min(420px, calc(100vw - 32px))",
        borderRadius: 14,
        padding: "12px 12px",
        boxShadow: "0 10px 30px rgba(0,0,0,0.18)",
        background: "#111827",
        color: "#fff",
        border: "1px solid rgba(255,255,255,0.08)",
        display: "flex",
        gap: 10,
        alignItems: "flex-start",
      },
      head: {
        display: "flex",
        alignItems: "center",
        gap: 8,
        fontSize: 13,
        opacity: 0.95,
        marginBottom: 4,
      },
      msg: { fontSize: 14, lineHeight: 1.5, wordBreak: "break-word" },
      close: {
        marginInlineStart: "auto",
        background: "transparent",
        border: "none",
        color: "rgba(255,255,255,0.75)",
        cursor: "pointer",
        padding: 6,
        borderRadius: 10,
      },
      barWrap: {
        height: 3,
        borderRadius: 999,
        background: "rgba(255,255,255,0.10)",
        overflow: "hidden",
        marginTop: 10,
      },
      bar: (pct, type) => {
        const base =
          type === "success"
            ? "#22c55e"
            : type === "error"
            ? "#ef4444"
            : type === "warning"
            ? "#f59e0b"
            : "#3b82f6";
        return {
          height: "100%",
          width: `${pct}%`,
          transition: "width 120ms linear",
          background: base,
        };
      },
    };
  }, []);

  function clearTimer(id) {
    const timer = timersRef.current.get(id);
    if (timer) clearInterval(timer);
    timersRef.current.delete(id);
  }

  function removeToast(id) {
    clearTimer(id);
    setItems((prev) => prev.filter((item) => item.id !== id));
  }

  useEffect(() => {
    const timers = timersRef.current;

    const off = onToast((payload) => {
      if (!payload?.message) return;

      const type = (payload.type || "info").toLowerCase();
      const durationMs = clamp(Number(payload.durationMs || 3200), 1200, 8000);
      const id =
        payload.id || `${Date.now()}_${Math.random().toString(16).slice(2)}_${type}`;

      setItems((prev) => {
        if (prev.some((item) => item.id === id)) return prev;

        const msgKey = `${type}::${payload.message}`;
        if (prev.some((item) => item.key === msgKey)) return prev;

        const next = [
          {
            id,
            type,
            message: String(payload.message),
            createdAt: Date.now(),
            durationMs,
            progress: 100,
            key: msgKey,
          },
          ...prev,
        ];

        return next.slice(0, 4);
      });

      const existing = timers.get(id);
      if (existing) clearInterval(existing);
      timers.delete(id);

      const start = Date.now();
      const tick = setInterval(() => {
        const elapsed = Date.now() - start;
        const pct = 100 - (elapsed / durationMs) * 100;

        if (pct <= 0) {
          const timer = timers.get(id);
          if (timer) clearInterval(timer);
          timers.delete(id);
          setItems((prev) => prev.filter((item) => item.id !== id));
          return;
        }

        setItems((prev) =>
          prev.map((item) => (item.id === id ? { ...item, progress: pct } : item))
        );
      }, 120);

      timers.set(id, tick);
    });

    return () => {
      off?.();
      timers.forEach((timer) => clearInterval(timer));
      timers.clear();
    };
  }, []);

  if (!items.length) return null;

  return (
    <div style={styles.wrap} aria-live="polite" aria-atomic="true">
      {items.map((toast) => {
        const meta = typeMeta(toast.type);
        return (
          <div key={toast.id} style={styles.toast} role="status">
            <div>
              <div style={styles.head}>
                <span aria-hidden="true">{meta.icon}</span>
                <strong>{meta.label}</strong>
              </div>
              <div style={styles.msg}>{toast.message}</div>
              <div style={styles.barWrap} aria-hidden="true">
                <div style={styles.bar(toast.progress, toast.type)} />
              </div>
            </div>

            <button
              type="button"
              onClick={() => removeToast(toast.id)}
              style={styles.close}
              aria-label="إغلاق"
              title="إغلاق"
            >
              ×
            </button>
          </div>
        );
      })}
    </div>
  );
}
